import React from 'react';
import { useAppConfig } from 'Components/utils/AppBootstrapper/AppConfigContext';
import { setUpCorrelationRuleSchemaValidationOptions } from 'Components/forms/BaseDetectionForm/BaseDetectionFormEditorSection/CorrelationRuleSchemaValidationOptions';
import { DataLakeType } from 'Source/constants';
import FormikEditorV3 from 'Components/fields/Editor/EditorV3';
import { analysisTypeToLabel } from 'Pages/Build/Detections/utils';
import { DetectionType, ListDetectionsSortFieldsEnum, SortDirEnum } from 'Generated/schema';
import { NetworkStatus } from '@apollo/client';
import { Field, FieldArray, useFormikContext } from 'formik';
import { AbstractButton, Button, ToggleButton, ToggleButtonGroup, Box, Flex } from 'pounceV2';
import AlertSettingsFormV3 from 'Pages/Build/Detections/CorrelationRules/AlertSettingsFormV3';
import useUrlParams from 'Hooks/useUrlParams';
import { useListDetectionIds } from 'Source/graphql/queries/listDetectionIds.generated';
import {
  checkIsValid,
  parseYAMLIntoSections,
  YAMLField,
} from 'Pages/Build/Detections/CorrelationRules/utils';
import { useToggleSequenceGroup } from 'Pages/Build/Detections/CorrelationRules/useToggleSequenceGroup';
import { SimpleRuleFormValues } from 'Pages/Build/Detections/Rules/SimpleRuleForm/SimpleRuleFormV3';
import { useTheme } from '@mui/material';
import UnitTestPanelV3, { addNewTestHelper } from './UnitTestPanelV3';

export enum EDITOR_TAB {
  YAML = 'yaml-editor',
  FORM = 'alert-form',
  TEST = 'unit-tests',
}

interface EditorPanelProps {
  readOnly?: boolean;
}

const EditorPanelV3 = ({ readOnly = false }: EditorPanelProps) => {
  const {
    palette: { componentTokens },
  } = useTheme();
  const { urlParams, updateUrlParams } = useUrlParams<{ section: EDITOR_TAB }>();
  const { values, setFieldValue } = useFormikContext<SimpleRuleFormValues>();
  const parsedYaml = React.useMemo(
    () => parseYAMLIntoSections(values.simpleDetectionBody),
    [values?.simpleDetectionBody],
  );
  const editorWrapper = React.useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = React.useState<number>(0);
  const { toggleSequenceGroup } = useToggleSequenceGroup();
  // Sequence correlation rules aren't supported on Databricks tenants —
  // the backend translates Sequence YAML to Group at SQL-gen time, so
  // letting the user toggle into a Sequence view would create a misleading
  // editing experience. Hide the toggle on Databricks; Group is the only
  // shape the editor exposes there.
  const { pantherDataLakeType } = useAppConfig();
  const allowSequenceToggle = pantherDataLakeType !== DataLakeType.Databricks;

  const isValid = React.useMemo(() => checkIsValid(parsedYaml), [parsedYaml]);

  React.useEffect(() => {
    const wrapper = editorWrapper.current;
    if (!wrapper) {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        setEditorHeight(entry.contentRect.height);
      });
    });

    resizeObserver.observe(wrapper);
    setEditorHeight(wrapper.clientHeight);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const reasonablePageSize = 300;
  const reasonableMaxDataSize = 10000;
  const {
    loading,
    previousData,
    data = previousData,
    fetchMore,
    networkStatus,
  } = useListDetectionIds({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        analysisTypes: [
          DetectionType.Rule,
          DetectionType.CorrelationRule,
          DetectionType.ScheduledRule,
        ],
        sortBy: ListDetectionsSortFieldsEnum.LastModified,
        sortDir: SortDirEnum.Descending,
        pageSize: reasonablePageSize,
      },
    },
  });

  React.useEffect(() => {
    if (
      !loading &&
      data &&
      networkStatus === NetworkStatus.ready &&
      data.detections?.detections?.length < data.detections?.paging?.totalItems &&
      data.detections?.detections.length < reasonableMaxDataSize
    ) {
      fetchMore({
        variables: {
          input: {
            page: data.detections.paging.thisPage + 1,
            pageSize: reasonablePageSize,
            analysisTypes: [
              DetectionType.Rule,
              DetectionType.CorrelationRule,
              DetectionType.ScheduledRule,
            ],
            sortBy: ListDetectionsSortFieldsEnum.LastModified,
            sortDir: SortDirEnum.Descending,
          },
        },
        // FIXME: The `updateQuery` option will be soon deprecated so we need to remove it
        // in favor of typePolicies provided they work as intended
        updateQuery: (previousResult, { fetchMoreResult }): typeof previousResult => {
          return {
            detections: {
              detections: [
                ...(previousResult?.detections.detections ?? []),
                ...fetchMoreResult.detections.detections,
              ],
              paging: fetchMoreResult?.detections?.paging,
            },
          };
        },
      });
    }
  }, [loading, data, networkStatus, fetchMore, reasonablePageSize, reasonableMaxDataSize]);

  const hintableDetectionIds = React.useMemo(() => {
    if (loading || !data) {
      return [];
    }
    const detectionItems = data.detections?.detections || [];
    return detectionItems.map(x => x.id);
  }, [loading, data]);

  // handles invalid urls -- if the section doesn't equal EDITOR_TAB.FORM, then default to YAML
  let section = EDITOR_TAB.YAML;
  if (urlParams?.section === EDITOR_TAB.FORM) {
    section = EDITOR_TAB.FORM;
  }
  if (urlParams?.section === EDITOR_TAB.TEST) {
    section = EDITOR_TAB.TEST;
  }

  const correlationRuleValidationSchema = React.useMemo(() => {
    return setUpCorrelationRuleSchemaValidationOptions(hintableDetectionIds);
  }, [hintableDetectionIds]);

  const correlationRuleYAMLEditorProps = React.useMemo(() => {
    return {
      as: FormikEditorV3,
      wordBasedSuggestions: false,
      placeholder: `# Enter the body of the ${analysisTypeToLabel(
        DetectionType.CorrelationRule,
      )} here...`,
      name: 'simpleDetectionBody',
      language: 'yaml',
      yamlValidationOptions: correlationRuleValidationSchema,
      required: true,
      'aria-describedby': 'editor-helper-text',
      readOnly,
    };
  }, [correlationRuleValidationSchema, readOnly]);

  const getToggledYAML = React.useCallback(() => {
    setFieldValue('simpleDetectionBody', toggleSequenceGroup(parsedYaml));
  }, [parsedYaml, setFieldValue, toggleSequenceGroup]);

  return (
    <>
      <Flex px={4} pt={6} justify="space-between">
        <ToggleButtonGroup
          value={urlParams.section || EDITOR_TAB.YAML}
          onChange={(_, value: EDITOR_TAB) => updateUrlParams({ section: value })}
        >
          <ToggleButton size="medium" value={EDITOR_TAB.YAML}>
            YAML Editor
          </ToggleButton>
          <ToggleButton size="medium" value={EDITOR_TAB.FORM}>
            Alert Settings
          </ToggleButton>
          <ToggleButton size="medium" value={EDITOR_TAB.TEST}>
            Unit Tests
          </ToggleButton>
        </ToggleButtonGroup>
        {isValid && !readOnly && section === EDITOR_TAB.YAML && allowSequenceToggle ? (
          <Button p={0} variant="link-primary" size="medium" onClick={() => getToggledYAML()}>
            Organize Rules as {parsedYaml?.Sequence?.length ? YAMLField.Group : YAMLField.Sequence}
          </Button>
        ) : null}
        {section === EDITOR_TAB.TEST && (
          <FieldArray
            name="tests"
            render={arrayHelpers =>
              !readOnly ? (
                <AbstractButton
                  color={componentTokens.buttons.colors.link.primary.text.resting}
                  _hover={{
                    color: componentTokens.buttons.colors.link.primary.text.hover,
                  }}
                  cursor="pointer"
                  onClick={() => addNewTestHelper(arrayHelpers, parsedYaml)}
                >
                  + Add new unit test
                </AbstractButton>
              ) : null
            }
          />
        )}
      </Flex>
      <Box ref={editorWrapper} height="100%" flexGrow={1} overflowY="hidden" p={4}>
        {section === EDITOR_TAB.YAML && (
          <Field
            maxHeight={editorHeight}
            minHeight={editorHeight}
            {...correlationRuleYAMLEditorProps}
          />
        )}
        {section === EDITOR_TAB.FORM && <AlertSettingsFormV3 readOnly={readOnly} />}
        {section === EDITOR_TAB.TEST && (
          <UnitTestPanelV3 parsedYaml={parsedYaml} readOnly={readOnly} />
        )}
      </Box>
    </>
  );
};

export default EditorPanelV3;
